import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Button, ScrollableIndicator } from '@vfit/shared/atoms';
import { Arrow } from '@vfit/shared-icons';
import { useDeviceType, useSwipeElement } from '@vfit/shared/hooks';
import { purify } from '@vfit/shared/themes';
import { checkWindow, AppMobile } from '@vfit/shared/data-access';
import { checkIsApp } from '@vfit/consumer/data-access';
import * as S from './stickyOffer.style';
import { IShowDetails, IStickyOffer } from './stickyOffer.model';
import { isDisabled, observeHeight, renderDescription } from './stickyOffer.utils';

const StickyOffer: React.FC<IStickyOffer> = ({
  topLabel,
  title,
  id,
  description,
  ctaActivation,
  secondCta,
  content,
  withProgressbar,
  getContentHeight,
  isLoading,
  isLoadingGoNext,
  slideBanner,
  hideDetailLabel,
  enableLiteUI,
  showDetailLabel,
  extraClass = ''
}) => {
  const [pTitle, pTopLabel] = purify([title, topLabel]);
  const [showDetails, setShowDetails] = useState<IShowDetails>('');
  const [activationBoxHeight, setActivationBoxHeight] = useState(0);
  const [ctaContainerWidth, setCtaContainerWidth] = useState(0);
  const activationBoxRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const ctaContainerRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const { isDesktop } = useDeviceType();
  const [tmpSlideBanner, setTmpSlideBanner] = useState(slideBanner);
  const [isPulsing, setIsPulsing] = useState(false);
  const isFirstRender = useRef(true);

  const observer = useRef(
    new ResizeObserver((entries) => {
      const { bottom, top } = entries[0].contentRect;
      const boxHeight = bottom + top;
      setActivationBoxHeight(boxHeight);

      // eslint-disable-next-line no-unsafe-optional-chaining
      setCtaContainerWidth(ctaContainerRef?.current?.clientWidth * 2 + 160);
      if (getContentHeight) {
        getContentHeight(boxHeight);
      }
    })
  );

  const stepProgress = useMemo(() => {
    if (withProgressbar?.show && withProgressbar.steps > 0) {
      return (withProgressbar.currentStep / withProgressbar.steps) * 100;
    }
    return 0;
  }, [withProgressbar]);

  useEffect(() => {
    const observerRef = observer.current;
    if (activationBoxRef && observerRef) {
      observerRef.observe(activationBoxRef.current);
    }
    return () => {
      if (observerRef) observerRef.disconnect();
    };
  }, [activationBoxRef, observer]);

  useEffect(() => {
    setTmpSlideBanner(slideBanner);
  }, [slideBanner]);

  const isApp = AppMobile.checkIsApp();

  const { onTouchStart, onTouchMove, onTouchEnd, swipeStatus } = useSwipeElement();

  useEffect(() => {
    if (isApp) {
      if (swipeStatus === 'up') setShowDetails('slide-up');
      if (swipeStatus === 'down') setShowDetails('slide-down');
    }
  }, [swipeStatus, isDesktop]);

  useEffect(() => {
    if (checkWindow()) {
      observeHeight(activationBoxRef);
    }
  }, []);

  const toggleDetails = useCallback(() => {
    if (showDetails !== 'slide-up') {
      setTmpSlideBanner(undefined);
      setShowDetails('slide-up');
    } else {
      setShowDetails('slide-down');
    }
  }, [showDetails]);

  useEffect(() => {
    if (isFirstRender.current) {
      // Skip the first render
      isFirstRender.current = false;
      // return;
    } else {
      setIsPulsing(true);
    }
  }, [title]);

  useEffect(() => {
    if (isPulsing == true) {
      setTimeout(() => setIsPulsing(false), 2100); // Match the duration of the animation
    }
  }, [isPulsing]);

  const renderShowDetail = () => {
    const desc = renderDescription(description, showDetails);
    if (!desc) return;
    return (
      <S.ShowDetailsCta
        rotation={showDetails === 'slide-up' ? '-90' : '90'}
        onClick={toggleDetails}
        data-name={`action_StickyOffer_details`}
      >
        <p>{desc}</p>
        <Arrow />
      </S.ShowDetailsCta>
    );
  };

  const renderActions = () => {
    if (enableLiteUI) {
      if (!isDesktop) return renderShowDetail();
      if (secondCta?.label) {
        return (
          <div
            style={{
              marginRight: 16,
            }}
          >
            <Button
              variant={'secondary'}
              onClick={() => {
                if (isLoadingGoNext) return;
                secondCta?.action?.();
              }}
              name={`action_StickyOffer_operatore`}
            >
              {secondCta.label}
            </Button>
          </div>
        );
      }
      return null;
    }
    return (
      <>
        {content && !isDesktop && renderShowDetail()}
        {content && isDesktop && (
          <div style={{ marginRight: '16px' }}>
            <Button onClick={toggleDetails} variant={'secondary'}>
              {showDetails !== 'slide-up'
                ? showDetailLabel || 'SCOPRI DETTAGLI'
                : hideDetailLabel || 'NASCONDI DETTAGLI'}
            </Button>
          </div>
        )}
      </>
    );
  };

  const DetailsController = useMemo(() => {
    if (isLoading) {
      return <S.DetailsLoading />;
    }

    return (
      <S.Details isApp={checkIsApp()} isPulsing={isPulsing}>
        <div className={extraClass}>
          {pTopLabel && (
            <S.TopLabel
              className="topLabel"
              dangerouslySetInnerHTML={{
                __html: pTopLabel,
              }}
            />
          )}
          {pTitle && (
            <p
              dangerouslySetInnerHTML={{
                __html: pTitle,
              }}
            />
          )}
          {enableLiteUI && isDesktop && renderShowDetail()}
        </div>
        {renderActions()}
      </S.Details>
    );
  }, [content, description, isDesktop, isLoading, showDetails, title, toggleDetails, isPulsing]);

  return (
    <>
      <S.OpaqueBackground
        id="opaque-background"
        isActive={!AppMobile.checkIsApp() ? '' : showDetails}
        onClick={() => setShowDetails('slide-down')}
      />
      <S.MainContainer
        id={id}
        isApp={AppMobile.checkIsApp()}
        activationBoxHeight={activationBoxHeight}
        className={`${showDetails} ${tmpSlideBanner || ''}`}
      >
        <S.ActivationBox
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          ref={activationBoxRef}
          withProgressbar={withProgressbar?.show}
        >
          {withProgressbar?.show && showDetails !== 'slide-up' && (
            <S.ProgressbarContainer>
              <S.Bar stepCoverage={stepProgress} />
            </S.ProgressbarContainer>
          )}
          {AppMobile.checkIsApp() && <ScrollableIndicator />}
          {DetailsController}
          <S.CtaContainer ref={ctaContainerRef} id="sticky-offer-next">
            {ctaActivation?.label && (
              <Button
                loading={isLoadingGoNext}
                disabled={isDisabled({
                  isLoadingGoNext,
                  variant: ctaActivation?.variant,
                  isDisabled: ctaActivation?.isDisabled,
                  isLoading,
                })}
                onClick={() => {
                  if (isLoadingGoNext) return;
                  if (showDetails === 'slide-up') setShowDetails('slide-down');
                  // actionClick is inhibited to avoid double url opening
                  !ctaActivation?.actionNavigationUrl && ctaActivation?.action?.();
                }}
                href={ctaActivation?.actionNavigationUrl}
                target={
                  ctaActivation?.actionNavigationUrl && ctaActivation?.isBlank === 'true'
                    ? '_blank'
                    : undefined
                }
                name={ctaActivation?.type ? `action_StickyOffer_${ctaActivation?.type}` : undefined}
                variant={
                  checkIsApp()
                    ? ctaActivation?.variant || 'mva:primary'
                    : ctaActivation?.variant || 'primary'
                }
              >
                {ctaActivation?.label}
              </Button>
            )}
          </S.CtaContainer>
        </S.ActivationBox>
        {content && showDetails === 'slide-up' && (
          <S.ChildrenContainer
            activationBoxHeight={activationBoxHeight}
            ctaContainerWidth={ctaContainerWidth}
          >
            <S.OverlayGlobalStyle />
            {content}
          </S.ChildrenContainer>
        )}
      </S.MainContainer>
    </>
  );
};

export default StickyOffer;
